import { supabase } from "@/integrations/supabase/client";

/**
 * Start a Stripe Checkout session for a `plans.id` row.
 * Auth-gates the user (redirects to /login with returnTo) when not signed in,
 * then redirects the browser to the returned Checkout URL.
 */
export async function startPlanCheckout(planId: string, opts?: {
  successPath?: string;
  cancelPath?: string;
  providerOverride?: "flutterwave";
}): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?returnTo=${returnTo}`;
    return;
  }

  const origin = window.location.origin;
  const success_url = `${origin}${opts?.successPath ?? "/checkout/success"}`;
  const cancel_url = `${origin}${opts?.cancelPath ?? "/checkout/cancel"}`;

  const { data, error } = await supabase.functions.invoke("create-checkout-session", {
    body: {
      plan_id: planId,
      success_url,
      cancel_url,
      ...(opts?.providerOverride ? { provider_override: opts.providerOverride } : {}),
    },
  });
  if (error) throw new Error(error.message);
  const url = (data as { checkout_url?: string; error?: string })?.checkout_url;
  if (!url) throw new Error((data as any)?.error ?? "checkout_failed");
  window.location.href = url;
}

/**
 * Start a Stripe Checkout session for a 3-installment plan (no interest).
 * Only valid for bootcamp plans priced ≥ €179.
 */
export async function startInstallmentCheckout(planId: string, opts?: {
  successPath?: string;
  cancelPath?: string;
}): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user) {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?returnTo=${returnTo}`;
    return;
  }

  const origin = window.location.origin;
  const success_url = `${origin}${opts?.successPath ?? "/checkout/success"}`;
  const cancel_url = `${origin}${opts?.cancelPath ?? "/checkout/cancel"}`;

  const { data, error } = await supabase.functions.invoke("create-installment-checkout", {
    body: { plan_id: planId, success_url, cancel_url },
  });
  if (error) throw new Error(error.message);
  const url = (data as { checkout_url?: string; error?: string })?.checkout_url;
  if (!url) throw new Error((data as any)?.error ?? "checkout_failed");
  window.location.href = url;
}

/** Compute 3-installment split from total cents; last installment absorbs remainder. */
export function splitInstallments(totalCents: number): [number, number, number] {
  const per = Math.floor(totalCents / 3);
  const last = totalCents - per * 2;
  return [per, per, last];
}

/** Map a bootcamp level to the canonical plan id. */
export function bootcampPlanIdFor(level?: string | null): string {
  switch ((level ?? "").toLowerCase()) {
    case "advanced":
      return "bootcamp.advanced";
    case "intermediate":
      return "bootcamp.intermediate";
    case "beginner":
    default:
      return "bootcamp.beginner";
  }
}

/** Canonical plan id for a microservice tier (kept in sync with the plans seed). */
export function microservicePlanIdFor(slug: string, tier: "basic" | "standard" | "premium"): string {
  return `microservice.${slug}.${tier}`;
}